/**
 * API Client for VizzEx External App
 */

import axios from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

// API base URL based on environment (backend API, not frontend app)
const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (import.meta.env.PROD ? 'https://hs-app.vizzex.com' : 'https://dev.hs-app.vizzex.com');

// Token storage keys
const TOKEN_KEY = 'vizzex_token';

/**
 * Get stored auth token
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store auth token
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Remove auth token
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getToken();
}

/**
 * Create axios instance with interceptors
 */
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      removeToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================
// API RESPONSE TYPES
// ============================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  error?: string;
  code?: string;
  [key: string]: T | boolean | string | undefined;
}

export interface User {
  id: number;
  email: string;
  name: string | null;
  avatarUrl?: string | null;
  emailVerified: boolean;
  primaryAuthProvider?: string;
  createdAt?: string;
}

export interface Organization {
  id: number;
  name: string;
  slug: string;
  billingEmail?: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  memberCount?: number;
  siteCount?: number;
}

export interface Site {
  id: number;
  organizationId: number;
  platform: string;
  platformId: string;
  siteUrl: string;
  siteName: string;
  cmsType?: string;
  blogArchiveUrl?: string;
  clientCmsPlatform?: string;
  licenseKey?: string;
  licenseStatus?: string;
  licenseTier?: string;
  licenseExpiresAt?: string;
  productSku?: string;
  claudeApiStatus?: string;
  status?: string;
  lastSyncAt?: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  error?: string;
  code?: string;
  user?: User;
  organization?: Organization;
  token?: string;
  requiresEmailVerification?: boolean;
  isNewUser?: boolean;
  otpRequired?: boolean;
}

// ============================================
// AUTH API
// ============================================

export const authApi = {
  /**
   * Register new user
   */
  register: async (email: string, password: string, name?: string): Promise<AuthResponse> => {
    const { data } = await api.post<AuthResponse>('/api/auth/register', { email, password, name });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  /**
   * Login with email/password
   */
  login: async (email: string, password: string): Promise<AuthResponse> => {
    const { data } = await api.post<AuthResponse>('/api/auth/login', { email, password });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  /**
   * Verify login OTP sent after account lockout
   */
  verifyLoginOtp: async (email: string, otp: string): Promise<AuthResponse> => {
    const { data } = await api.post<AuthResponse>('/api/auth/verify-login-otp', { email, otp });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  /**
   * Get current user
   */
  getCurrentUser: async (): Promise<{
    success: boolean;
    user?: User;
    organizations?: Organization[];
    currentOrganizationId?: number;
    currentSite?: Site;
  }> => {
    const { data } = await api.get('/api/auth/me');
    return data;
  },

  /**
   * Logout
   */
  logout: async (): Promise<void> => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      removeToken();
    }
  },

  /**
   * Request magic link
   */
  requestMagicLink: async (email: string): Promise<{ success: boolean; message?: string }> => {
    const { data } = await api.post('/api/auth/magic-link', { email });
    return data;
  },

  /**
   * Verify magic link
   */
  verifyMagicLink: async (token: string): Promise<AuthResponse> => {
    const { data } = await api.get<AuthResponse>(`/api/auth/magic-link/verify?token=${token}`);
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  /**
   * Request password reset
   */
  requestPasswordReset: async (email: string): Promise<{ success: boolean; message?: string }> => {
    const { data } = await api.post('/api/auth/password-reset', { email });
    return data;
  },

  /**
   * Reset password
   */
  resetPassword: async (token: string, password: string): Promise<{ success: boolean; message?: string }> => {
    const { data } = await api.post('/api/auth/password-reset/confirm', { token, password });
    return data;
  },

  /**
   * Change password (authenticated — requires current password)
   */
  changePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message?: string; token?: string }> => {
    const { data } = await api.post('/api/auth/change-password', { currentPassword, newPassword });
    if (data.token) {
      setToken(data.token);
    }
    return data;
  },

  /**
   * Verify auto-login token (from HubSpot app redirect)
   */
  verifyAutoLogin: async (token: string): Promise<AuthResponse> => {
    const { data } = await api.post<AuthResponse>('/api/auth/auto-login/verify', { token });
    // Don't set token here - let the component handle it to avoid redirect loop
    return data;
  },

  /**
   * Get HubSpot OAuth URL
   */
  getHubSpotOAuthUrl: (): string => {
    const clientId = import.meta.env.VITE_HUBSPOT_CLIENT_ID;
    const redirectUri = `${window.location.origin}/oauth/callback/hubspot`;
    const scopes = 'oauth content';
    return `https://app.hubspot.com/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=${encodeURIComponent(scopes)}`;
  },

  /**
   * Connect a blog by URL (No-CMS / HTML client)
   */
  connectHtmlSite: async (payload: {
    blogArchiveUrl: string;
    licenseKey: string;
    claudeApiKey: string;
    siteName?: string;
    clientCmsPlatform?: string;
    clientCmsPlatformOther?: string;
  }): Promise<{ success: boolean; site?: Site; error?: string }> => {
    const { data } = await api.post('/api/auth/connect-html-site', payload);
    return data;
  },
};

// ============================================
// ORGANIZATIONS API
// ============================================

export const organizationsApi = {
  /**
   * Get user's organizations
   */
  getAll: async (): Promise<{ success: boolean; organizations: Organization[] }> => {
    const { data } = await api.get('/api/organizations');
    return data;
  },

  /**
   * Get organization by ID
   */
  get: async (id: number): Promise<{ success: boolean; organization: Organization; userRole: string }> => {
    const { data } = await api.get(`/api/organizations/${id}`);
    return data;
  },

  /**
   * Update organization
   */
  update: async (id: number, updates: Partial<Organization>): Promise<{ success: boolean; organization: Organization }> => {
    const { data } = await api.put(`/api/organizations/${id}`, updates);
    return data;
  },

  /**
   * Get organization members
   */
  getMembers: async (id: number): Promise<{ success: boolean; members: User[] }> => {
    const { data } = await api.get(`/api/organizations/${id}/members`);
    return data;
  },
};

// ============================================
// SITES API
// ============================================

export const sitesApi = {
  /**
   * Get organization sites
   */
  getByOrganization: async (organizationId: number): Promise<{ success: boolean; sites: Site[] }> => {
    const { data } = await api.get(`/api/organizations/${organizationId}/sites`);
    return data;
  },

  /**
   * Get site by ID
   */
  get: async (id: number): Promise<{ success: boolean; site: Site; userRole: string }> => {
    const { data } = await api.get(`/api/sites/${id}`);
    return data;
  },

  /**
   * Get current site
   */
  getCurrent: async (): Promise<{ success: boolean; site: Site | null }> => {
    const { data } = await api.get('/api/sites/current');
    return data;
  },

  /**
   * Switch current site
   */
  switch: async (siteId: number): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post('/api/sites/switch', { siteId });
    return data;
  },

  /**
   * Update a site (siteName, metadata; blogArchiveUrl / html post selection for html clients only)
   */
  update: async (id: number, payload: {
    siteName?: string;
    blogArchiveUrl?: string;
    metadata?: Record<string, unknown>;
    /** HTML-client post selection (migration 065) — editable outside of an analysis run. */
    htmlMaxPosts?: number | null;
    htmlSelectedCategories?: string[] | null;
  }): Promise<{
    success: boolean;
    site?: Partial<Site> & {
      htmlPostSelection?: { maxPosts: number | null; categories: string[] | null; configured: boolean };
    };
    error?: string;
  }> => {
    const { data } = await api.put(`/api/sites/${id}`, payload);
    return data;
  },

  /**
   * Get site license status
   */
  getLicenseStatus: async (id: number): Promise<{
    success: boolean;
    license: {
      hasKey: boolean;
      status: string;
      tier: string;
      expiresAt?: string;
      productSku?: string;
    };
    features: {
      analysisLimit: number;
      semanticLinks: boolean;
      schemaGeneration: boolean;
      teamMembers: boolean;
    };
  }> => {
    const { data } = await api.get(`/api/sites/${id}/license`);
    return data;
  },
};

// ============================================
// TEAM API
// ============================================

export interface TeamSite {
  id: number;
  name: string;
}

export interface TeamMember {
  id: number;
  email: string;
  name: string | null;
  avatarUrl: string | null;
  authProvider: string | null;
  role: string;
  joinedAt: string | null;
  lastLoginAt: string | null;
  sites: TeamSite[] | null;
}

export interface PendingInvitation {
  id: number;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
  invitedByName: string | null;
  invitedByEmail: string | null;
  sites: TeamSite[] | null;
}

export interface InvitationInfo {
  email: string;
  role: string;
  organizationName: string;
  organizationId: number;
  expiresAt: string;
  siteIds: number[];
  sites: TeamSite[];
}

export const teamApi = {
  /**
   * Invite a member to specific sites
   */
  invite: async (organizationId: number, email: string, siteIds: number[]): Promise<{ success: boolean; message: string }> => {
    const { data } = await api.post(`/api/organizations/${organizationId}/invite`, { email, siteIds });
    return data;
  },

  /**
   * List active members and pending invitations
   */
  getMembers: async (organizationId: number): Promise<{ success: boolean; members: TeamMember[]; pending: PendingInvitation[] }> => {
    const { data } = await api.get(`/api/organizations/${organizationId}/members`);
    return data;
  },

  /**
   * Remove an active member
   */
  removeMember: async (organizationId: number, userId: number): Promise<{ success: boolean }> => {
    const { data } = await api.delete(`/api/organizations/${organizationId}/members/${userId}`);
    return data;
  },

  /**
   * Revoke a pending invitation
   */
  revokeInvitation: async (organizationId: number, invitationId: number): Promise<{ success: boolean }> => {
    const { data } = await api.delete(`/api/organizations/${organizationId}/invitations/${invitationId}`);
    return data;
  },

  /**
   * Get invitation info (and auto-accept if user is authenticated)
   */
  getInvitation: async (token: string): Promise<{
    success: boolean;
    requiresAuth?: boolean;
    hasExistingAccount?: boolean;
    emailMismatch?: boolean;
    organizationId?: number;
    siteIds?: number[];
    invitation?: InvitationInfo;
  }> => {
    const { data } = await api.get(`/api/invitations/accept?token=${token}`);
    return data;
  },

  /**
   * Accept invitation with password setup (new users)
   */
  acceptWithPassword: async (token: string, name: string, password: string): Promise<{
    success: boolean;
    token?: string;
    organizationId?: number;
    siteIds?: number[];
  }> => {
    const { data } = await api.post('/api/invitations/accept', { token, name, password });
    return data;
  },

  // Legacy — kept for compatibility with existing InvitationPage
  acceptInvitation: async (token: string): Promise<{
    success: boolean;
    message?: string;
    requiresAuth?: boolean;
    hasExistingAccount?: boolean;
    organizationId?: number;
    invitation?: InvitationInfo;
  }> => {
    const { data } = await api.get(`/api/invitations/accept?token=${token}`);
    return data;
  },

  /**
   * Remove member (legacy alias)
   */
  remove: async (organizationId: number, userId: number): Promise<{ success: boolean }> => {
    const { data } = await api.delete(`/api/organizations/${organizationId}/members/${userId}`);
    return data;
  },
};

export default api;

